import React, { useState } from 'react';
import { Box, Typography, Button, FormControlLabel, Checkbox, Radio, RadioGroup, Modal } from '@mui/material';
import { submitQuiz } from '../../services/lessonService';
import { markLessonComplete } from '../../services/lessonService';
import { useNotification } from '../../context/NotificationContext';

function Quiz({ lesson }) {
    const { showNotification } = useNotification();
    const { quizWithQuestions } = lesson;
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [quizResult, setQuizResult] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [loading, setLoading] = useState(false);

    // Handle selecting answers (Single & Multiple Choice)
    const handleSelectAnswer = (questionId, optionId, allowMultipleSelection) => {
        setSelectedAnswers((prev) => {
            const currentAnswers = prev[questionId] || [];
            if (allowMultipleSelection) {
                return {
                    ...prev,
                    [questionId]: currentAnswers.includes(optionId)
                        ? currentAnswers.filter((id) => id !== optionId)
                        : [...currentAnswers, optionId]
                };
            } else {
                return { ...prev, [questionId]: [optionId] };
            }
        });
    };

    // Handle Quiz Submission
    const handleSubmit = async () => {
        if (Object.keys(selectedAnswers).length < quizWithQuestions.questions.length) {
            alert("Please answer all questions before submitting.");
            return;
        }

        setLoading(true);
        const responses = Object.keys(selectedAnswers).map((questionId) => ({
            questionId,
            selectedOptions: selectedAnswers[questionId]
        }));

        const result = await submitQuiz(quizWithQuestions._id, responses);
        setLoading(false);

        if (result.success) {
            setQuizResult(result.data);
            setOpenModal(true);
        } else {
            alert(result.message || "An error occurred while submitting the quiz.");
        }
    };

    // Handle marking lesson as complete
    const handleContinue = async () => {
        setLoading(true);
        const result = await markLessonComplete(lesson._id);
        setLoading(false);

        if (result.success) {
            const { progress, nextLesson } = result.data;
            if (!progress.completed && nextLesson) {
                window.location.assign(`/course/${lesson.course.slug}/lesson/${nextLesson._id}`);
            } else if (progress.completed) {
                showNotification("Congratulations! You have completed the course.", "success");
                window.location.assign(`/course/${lesson.course.slug}/completed`);
            } else {
                showNotification("Lesson not available. Please try again.", "error");
            }
        } else {
            showNotification("An error occurred. Please try again.", "error");
        }
    };

    // Reset quiz for retry
    const handleRetry = () => {
        setSelectedAnswers({});
        setQuizResult(null);
        setOpenModal(false);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
                {quizWithQuestions.title}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
                {quizWithQuestions.instructions}
            </Typography>

            {quizWithQuestions.questions.map((question) => (
                <Box key={question._id} sx={{ mb: 4, p: 2, border: '1px solid #ddd', borderRadius: '8px' }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                        {question.questionText}
                    </Typography>

                    {question.allowMultipleSelection ? (
                        <Box>
                            {question.options.map((option) => (
                                <FormControlLabel
                                    key={option.optionId}
                                    control={
                                        <Checkbox
                                            checked={selectedAnswers[question._id]?.includes(option.optionId) || false}
                                            onChange={() =>
                                                handleSelectAnswer(question._id, option.optionId, true)
                                            }
                                        />
                                    }
                                    label={option.text}
                                />
                            ))}
                        </Box>
                    ) : (
                        <RadioGroup
                            value={selectedAnswers[question._id]?.[0] || ''}
                            onChange={(e) => handleSelectAnswer(question._id, e.target.value, false)}
                        >
                            {question.options.map((option) => (
                                <FormControlLabel
                                    key={option.optionId}
                                    value={option.optionId}
                                    control={<Radio />}
                                    label={option.text}
                                />
                            ))}
                        </RadioGroup>
                    )}
                </Box>
            ))}

            <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                    textTransform: 'none',
                    fontSize: { xs: '0.8rem', sm: '0.8rem', md: '0.9rem', lg: '1rem' },
                    fontWeight: 600,
                    borderRadius: '30px',
                    px: { xs: 1.5, sm: 2, md: 3 },
                    py: { xs: 0.8, sm: 1, md: 1.5 },
                    backgroundColor: 'primary.main',
                    color: 'common.white',
                    '&:hover': {
                        backgroundColor: 'primary.dark',
                    },
                    transition: 'all 0.3s ease',
                }}
            >
                {loading ? "Submitting..." : "Submit Quiz"}
            </Button>

            {/* Modal for Quiz Results */}
            <Modal open={openModal} onClose={() => setOpenModal(false)}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        textAlign: 'center',
                        borderRadius: '12px',
                    }}
                >
                    <Typography variant="h5" fontWeight="bold">
                        {quizResult?.passed ? "Quiz Passed!" : "Quiz Failed"}
                    </Typography>
                    <Typography sx={{ mt: 2 }}>
                        Score: {quizResult?.score}%
                    </Typography>
                    <Typography sx={{ mt: 1, color: quizResult?.passed ? "green" : "red" }}>
                        {quizResult?.passed ? "Congratulations! You can proceed." : "You need to retry the quiz."}
                    </Typography>

                    <Box sx={{ mt: 3 }}>
                        {quizResult?.passed ? (
                            <Button variant="contained" size="large" color="success" onClick={handleContinue}>
                                Continue
                            </Button>
                        ) : (
                            <Button variant="contained" size="large" color="error" onClick={handleRetry}>
                                Retry Quiz
                            </Button>
                        )}
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}

export default Quiz;
